import cloneDeep from 'lodash/cloneDeep';

import { surveyDatePromptIds } from '@api/surveys/utilities/constants';

import {
  FIELDS_USING_ID,
  MODEL_TYPES,
  PLACEHOLDER_IMAGE,
  PURCHASE_VOLUME_TYPES,
  QUESTION_LOGIC_OPERATORS,
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_OPTIONS,
  SURVEY_HELP_URL,
} from './constants';

/**
 * @typedef {Object} InstantSurvey
 * @property {String} country 'ca' | 'us'
 * @property {Group[]} groups
 * @property {Number} [id]
 * @property {String} instantSurveyType
 * @property {?Date} issueDateTime
 * @property {SurveyQuestionLogic} questionLogic
 * @property {Question[]} questions
 * @property {String} [status]
 * @property {?Number} surveyTemplateId
 * @property {String} title
 * @property {Object} [addons]
 * @property {Boolean} [use_promo_credits]
 */

/**
 * @typedef {Object} Group
 * @property {?Number} expectedCompletes
 * @property {?String} genRepSampleType
 * @property {Number|String} id
 * @property {String} name
 * @property {Object[]} promptAnswers
 * @property {?String} quotaGroupRef
 * @property {Number} quotaLimit
 * @property {String} validationStatus
 * @property {?Number} validationUniqueUsers
 * @property {?Number} validationUsers
 */

/**
 * @typedef {Object} Logic
 * @property {Object[]} answers
 * @property {LogicOperator} operator
 * @property {Number} [question]
 *
 * @typedef {'ALL'|'ANY'|'NOT'} LogicOperator
 *
 * @typedef {Object} QuestionLogic
 * @property {Logic} [disqualify]
 * @property {Object} [skip]
 * @property {Logic[]} skip.logic
 * @property {LogicOperator} skip.operator
 *
 * @typedef {Object.<number, QuestionLogic>} SurveyQuestionLogic
 */

/**
 * formatInstantSurvey
 * takes an in-application InstantSurvey and formats it for create/update requests
 * @param {InstantSurvey} instantSurvey
 * @return {Object}
 */
export const formatInstantSurvey = ({
  country,
  groups,
  id,
  instantSurveyType: instant_survey_type,
  launchMethod: launch_method,
  questionLogic: question_logic,
  questions,
  researchObjectives: research_objectives,
  status,
  surveyTemplateId: survey_template_id,
  title,
}) => {
  return {
    country,
    groups: groups.map(formatInstantSurveyGroup),
    id,
    instant_survey_type,
    launch_method,
    question_logic: formatQuestionLogic(question_logic),
    questions: questions.map(formatQuestion),
    research_objectives,
    status,
    survey_template_id,
    title,
  };
};

/**
 * formatInstantSurveyGroup
 * prepares an in-app survey group to be sent to the instant survey service
 * @param {Group} group
 * @return {Object}
 */
const formatInstantSurveyGroup = ({
  _delete,
  conceptImage: concept_image,
  conceptName: concept_name,
  genRepSampleType,
  id,
  name,
  promptAnswers,
  quotaLimit: quota_limit,
}) => ({
  _delete,
  concept_image,
  concept_name,
  gen_rep_sample_type: genRepSampleType,
  id: typeof id === 'string' ? undefined : id, // remove temporary uids
  prompt_answers: promptAnswers
    .filter(({ _delete, id }) => !_delete || (_delete && id)) // only delete previously saved answers
    .map(({ _delete, field, fieldType, id, operator, promptId, values }) => {
      let value;
      if (_delete) {
        value = '_delete';
      } else if (fieldType === 'DATE') {
        if (values.length === 1 && typeof values[0] === 'string') {
          value = values[0];
        } else if (values.length === 2 && typeof values[0] === 'string') {
          value = values.join(';');
        } else {
          value = values[0]?.date_range?.join(';');
        }
      } else if (fieldType === 'NUMERIC' && Object.values(PURCHASE_VOLUME_TYPES).includes(field)) {
        value = Array.isArray(values) ? values.join(';') : values;
      } else if (field === 'new_item') {
        value = values?.map((item) => item?.value || item?.id).join(';');
      } else {
        value = values
          ?.map(({ id, name }) => (FIELDS_USING_ID.includes(field) ? id : name))
          .join(';');
      }

      return {
        _delete,
        fe_notes: { insights_values: values },
        field,
        field_type: fieldType,
        id,
        operator,
        prompt_id: promptId,
        value,
      };
    }),
  quota_limit,
  title: name,
});

/**
 * @typedef {Object} Question
 * @property {Object[]} answers
 * @property {String} [caption]
 * @property {Number} folder
 * @property {Number} [id]
 * @property {Object} [image]
 * @property {String} [image.imageBucketName]
 * @property {String} [image.imageCdnUrl]
 * @property {String} [image.imageObjectKey]
 * @property {String} [image.imageObjectType]
 * @property {String[]} instantSurveyTypes
 * @property {Object} properties
 * @property {Boolean} [properties.forceCurrency]
 * @property {Boolean} [properties.forceNumeric]
 * @property {Boolean} [properties.forcePercent]
 * @property {Number} [properties.maxAnswers]
 * @property {Number} [properties.maxNumber]
 * @property {Number} [properties.maxTotal]
 * @property {Number} [properties.minNumber]
 * @property {Boolean} [properties.mustBeMax]
 * @property {Boolean} [properties.onlyWholeNum]
 * @property {Boolean} [properties.randomizeOptions]
 * @property {Number} [questionSourceId]
 * @property {Question[]} subQuestions
 * @property {String} text
 * @property {String} type
 */

/**
 * formatQuestion
 * @param {Question} question
 * @return {Object}
 */
export const formatQuestion = ({
  aiGenerated: ai_generated,
  answers: options,
  image,
  instantSurveyTypes: instant_survey_types,
  properties,
  showConceptImage,
  subQuestions,
  type: question_type,
  ...rest
}) => {
  const isTurf = question_type === QUESTION_TYPES.TURF.value;
  return {
    ai_generated,
    image: image ? { id: image.id } : null,
    instant_survey_types,
    options: options?.map(
      ({
        _delete,
        id,
        image,
        isAnchored: anchored,
        isExclusive: exclusive,
        isNoneOfTheAbove: none_of_the_above,
        isOther: other,
        text,
      }) => {
        return {
          _delete,
          anchored,
          exclusive,
          id: typeof id === 'string' ? undefined : id,
          image: image ? { id: image.id } : null,
          none_of_the_above,
          other,
          text,
        };
      }
    ),
    properties: {
      // INSRV-3818: only send relevant properties based on question type
      ...(question_type === QUESTION_TYPES.CHECKBOX.value && {
        max_answers: properties?.maxAnswers,
      }),
      ...(question_type === QUESTION_TYPES.CONTSUM.value && {
        force_currency: properties?.forceCurrency,
        force_numeric: properties?.forceNumeric,
        force_percent: properties?.forcePercent,
        max_number: properties?.maxNumber,
        max_total: properties?.maxTotal,
        min_number: properties?.minNumber,
        must_be_max: properties?.mustBeMax,
        only_whole_num: properties?.onlyWholeNum,
      }),
      ...(question_type === QUESTION_TYPES.MAX_DIFF.value && {
        best_label: properties?.bestLabel,
        number_of_sets: properties?.numberOfSets,
        options_per_set: properties?.optionsPerSet,
        worst_label: properties?.worstLabel,
      }),
      ...(question_type === QUESTION_TYPES.NUMERIC.value && {
        force_currency: properties?.forceCurrency,
        force_numeric: true, // INSRV-3041: Field must be true for Numeric question logic to be applied
        force_percent: properties?.forcePercent,
        max_number: properties?.maxNumber,
        min_number: properties?.minNumber,
        only_whole_num: properties?.onlyWholeNum,
      }),
      ...(QUESTION_TYPES_WITH_OPTIONS.includes(question_type) && {
        randomize_options: properties?.randomizeOptions,
      }),
    },
    question_type: isTurf ? QUESTION_TYPES.CHECKBOX.value : question_type,
    randomize_options: false, // INSRV-2367
    show_concept_image: showConceptImage,
    sub_questions: subQuestions?.map(({ _delete, id, text, type: question_type }) => ({
      _delete,
      id: typeof id === 'string' ? undefined : id,
      question_type,
      text,
    })),
    turf: isTurf,
    ...rest,
  };
};

/**
 * formatQuestionLogic
 * @param {Object} questionLogic
 * @return {Object}
 */
export const formatQuestionLogic = (questionLogic) => {
  const modifiedQuestionLogic = cloneDeep(questionLogic);
  for (const questionId in questionLogic) {
    const question = modifiedQuestionLogic[questionId];

    if (Object.keys(question).length > 0) {
      if (question.disqualify) {
        question.disqualify.answers = question.disqualify.answers.map((answer) => answer.id);
        question.disqualify.operator = question.disqualify.operator.value;

        delete question.disqualify.hasAnswerError;
        delete question.disqualify.isFirstAnswerError;
      }

      if (question.skip) {
        question.skip.operator = question.skip.operator.value;
        question.skip.logic = question.skip.logic.map((row) => {
          if (row.url_variable === 'quota_group_ref') {
            return {
              operator: row.operator.value,
              url_variable: 'quota_group_ref',
              values: row.values,
            };
          } else if (row.hasOwnProperty('comparison_value')) {
            return {
              comparison_value: row.comparison_value,
              operator: row.operator.value,
              question: row.question?.id,
            };
          } else {
            return {
              answers: row.answers.map((answer) => answer.id),
              operator: row.operator.value,
              question: row.question?.id,
            };
          }
        });
      }
    } else {
      delete modifiedQuestionLogic[questionId];
    }
  }
  return modifiedQuestionLogic;
};

/**
 * isDefined
 * @param {*} value
 * @return {Boolean}
 */
const isDefined = (value) => typeof value !== 'undefined';

/**
 * transformAskWhyQuestions
 * @param {Object[]} response
 * @param {Object[]} response.results
 * @return {Question[]}
 */
export const transformAskWhyQuestions = ({ results }) => results.map(transformQuestion);

/**
 * @typedef {Object} ExternalQuestion
 * @property {Number} id
 * @property {Object[]} options
 * @property {Number} options.id
 * @property {String} options.title
 * @property {String} options.value
 * @property {Object[]} [subQuestions]
 * @property {String} subQuestions.id
 * @property {String} subQuestions.title
 * @property {String} title
 * @property {String} type
 */

/**
 * transformExternalQuestion
 * @param {Object} question
 * @param {Boolean?} question.comment
 * @param {String[]} question.description empty in example referenced
 * @param {Boolean?} question.has_showhide_deps
 * @param {Number} question.id
 * @param {Object[]} question.options
 * @param {Number} question.options.id
 * @param {String} question.options.title
 * @param {String} question.options.value appears to be the same as title
 * @param {String} [question.pipe_values_from]
 * @param {Object?} question.properties eg. { hidden, labels_right, map_key, option_sort, orientation, required }
 * @param {String} question.shortname deprecated
 * @param {Object[]} [question.sub_questions]
 * @param {Number} question.sub_questions.id
 * @param {String} question.sub_questions.title
 * @param {String} question.title
 * @param {Boolean?} question.turf
 * @param {String} question.type
 * @param {Array|Object} question.varname
 * @return {ExternalQuestion}
 */
export const transformExternalQuestion = ({ id, options, sub_questions, title, turf, type }) => ({
  id,
  options: options.map(({ id, title, value }) => ({ id, title, value })),
  subQuestions: sub_questions?.map(({ id, title }) => ({ id, title })),
  title,
  turf,
  type,
});

/**
 * transformExternalQuestions
 * @param {Object[]} questions
 * @return {ExternalQuestion[]}
 */
export const transformExternalQuestions = (questions) => questions.map(transformExternalQuestion);

/**
 * transformSurveyPrompt
 * @param {Object} prompt
 * @param {Boolean} prompt.advanced
 * @param {Boolean} [prompt.allow_custom_date]
 * @param {String} prompt.display_name
 * @param {Date} [prompt.ecomm_delay_date]
 * @param {Number} [prompt.ecomm_lag_period_in_days]
 * @param {String} prompt.empty_value
 * @param {Date} [prompt.general_lag_date]
 * @param {Number} prompt.id
 * @param {Boolean} prompt.in_modal
 * @param {String} prompt.insights_key
 * @param {String} prompt.instant_survey_type
 * @param {Boolean} prompt.is_prefix_enabled
 * @param {Number} [prompt.lag_period_in_days]
 * @param {Number} [prompt.max_values]
 * @param {Number} [prompt.min_answers]
 * @param {String} prompt.prompt_key
 * @param {Object} prompt.prompt_type
 * @param {Boolean} prompt.required
 * @param {Boolean} prompt.required_for_modal
 * @param {Boolean} prompt.search_enabled
 * @param {Boolean} prompt.sort_enabled
 * @return {Object}
 */
export const transformSurveyPrompt = ({
  advanced: isAdvanced,
  allow_custom_date: hasCustomDateOption,
  behavior_group: behaviorGroup,
  display_name: name,
  empty_value: emptyValue,
  general_lag_date: maxDate,
  id,
  in_modal: isModalPrompt,
  insights_key: insightsKey,
  // instant_survey_type,
  is_prefix_enabled: isPrefixIncluded,
  max_values: maxValues,
  min_answers: minAnswers,
  prompt_key: promptKey,
  prompt_type: promptType,
  required: isRequired,
  required_for_modal: isRequiredForModal,
  search_enabled: isSearchEnabled,
  sort_enabled: isSortEnabled,
}) => {
  const isDateRange = surveyDatePromptIds.includes(insightsKey);
  const isComparisonPeriod = insightsKey === 'comparison_period';
  return {
    ...(promptType.fields?.length && {
      attributes: promptType.fields.map(
        ({
          description,
          field,
          insights_id: id,
          is_custom: isCustom,
          is_fixed: isFixed,
          is_placeholder: isPlaceholder,
          name,
        }) => ({
          description,
          field,
          id,
          isCustom,
          isFixed,
          isPlaceholder,
          name,
        })
      ),
    }),
    ...(isComparisonPeriod
      ? {
          dateOptions: promptType.options?.map((option) => ({
            options: [
              {
                isDefault: option.is_default,
                label: option.display,
                value: [option.key],
              },
            ],
            timeFrame: option.display,
          })),
        }
      : {}),
    ...(isDateRange
      ? {
          dateOptions: promptType.options?.map(
            ({ in_modal: isInModal, options = [], time_frame: timeFrame, tooltip }) => ({
              isInModal,
              options: options.map(({ date_range, label }) => {
                return {
                  label,
                  value: date_range,
                };
              }),
              timeFrame,
              type: tooltip,
            })
          ),
        }
      : {}),
    behaviorGroup,
    emptyValue,
    hasCustomDateOption,
    id,
    insightsKey,
    isAdvanced,
    isModalPrompt,
    isPrefixIncluded,
    isRequired,
    isRequiredForModal,
    isSearchEnabled,
    isSortEnabled,
    maxDate,
    maxValues: isDateRange ? 1 : maxValues,
    minAnswers,
    name,
    ...(!isDateRange && !isComparisonPeriod && promptType.options?.length > 0
      ? {
          objects: promptType.options.map(({ display: name, key: id }) => ({
            field: promptType?.option_field,
            id,
            name,
          })),
        }
      : {}),
    promptKey,
  };
};

/**
 * transformQuestionnaireJob
 * @param {Object} response
 * @return {Object}
 */
export const transformQuestionnaireJobResponse = (response) => {
  const questions = response.output?.questions.map((question, index) => ({
    aiGenerated: true,
    answers: question.options
      ? question.options.map((opt, i) => ({
          id: `opt-${index}-${i}`,
          text: opt.text,
          ...opt,
        }))
      : [],
    id: `question-${index}`,
    subQuestions: question.sub_questions || [],
    text: question.name,
    type: question.question_type,
  }));

  return {
    output: {
      questions,
    },
    status: response.status,
  };
};

/**
 * transformSurveyQuestionResponseSummaryResponses
 * @param {Object} response
 * @param {Object[]} response.results
 * @return {Object}
 */
export const transformSurveyQuestionResponseSummaryResponses = (response) => {
  const questionSummariesMap = {};
  let wholeSurveySummary = null;

  response.results.forEach(
    ({
      active,
      additional_params: additionalParams,
      admin_note: adminNote,
      created,
      id,
      insights_job_ref: insightsJobRef,
      model_used: modelUsed,
      output,
      parent_job: parentJob,
      prompts,
      report_type: reportType,
      status,
      status_message: statusMessage,
      updated,
      user_left_feedback: userLeftFeedback,
      user_left_negative_feedback: userLeftNegativeFeedback,
    }) => {
      const formattedSummary = {
        active,
        additionalParams: {
          compareBy: additionalParams?.compare_by,
          compareByName: additionalParams?.compare_by_name,
          compareByValue: additionalParams?.compare_by_value,
          insightsSurveyId: additionalParams?.insights_survey_id,
          questionId: additionalParams?.question_id,
          questionSourceId: additionalParams?.question_source_id,
          region: additionalParams?.region,
          surveyId: additionalParams?.survey_id,
        },
        adminNote,
        created,
        id,
        insightsJobRef,
        modelUsed,
        output,
        parentJob,
        prompts,
        reportType,
        status,
        statusMessage,
        updated,
        userLeftFeedback,
        userLeftNegativeFeedback,
      };

      if (reportType === 'WHOLE_SURVEY') {
        wholeSurveySummary = formattedSummary;
        return;
      }

      const questionSourceId = additionalParams?.question_source_id;
      if (!questionSummariesMap[questionSourceId]) {
        questionSummariesMap[questionSourceId] = [formattedSummary];
      } else {
        questionSummariesMap[questionSourceId].push(formattedSummary);
      }
    }
  );

  return {
    questionSummaries: questionSummariesMap,
    wholeSurveySummary,
  };
};

/**
 * transformImageObject
 * @param {Object} image
 * @param {String} image.bucket_name
 * @param {String} image.cdn_url
 * @param {Number} image.id
 * @param {String} image.object_key
 * @param {String} image.object_type
 * @param {Object} [image.fields]
 * @param {String} image.url
 * @return {Object}
 */
export const transformImageObject = ({
  bucket_name: bucketName,
  cdn_url: cdnUrl,
  fields,
  id,
  object_key: objectKey,
  object_type: objectType,
  url,
}) => ({
  bucketName,
  cdnUrl: cdnUrl.indexOf('http://localhost') !== -1 ? PLACEHOLDER_IMAGE : cdnUrl,
  fields,
  id,
  objectKey,
  objectType,
  url,
});

/**
 * transformInstantSurvey
 * @param {Object} instantSurvey
 * @param {Object} instantSurvey.ask_why_value_map
 * @param {?Date} instantSurvey.build_quota_groups_datetime
 * @param {?String} instantSurvey.contact_list_ref
 * @param {String} instantSurvey.country
 * @param {Date} instantSurvey.created_at
 * @param {?Date} instantSurvey.expire_datetime
 * @param {Object[]} instantSurvey.groups
 * @param {Numer} instantSurvey.id
 * @param {?Number} instantSurvey.insights_survey_ref
 * @param {String} instantSurvey.instant_survey_type
 * @param {?Date} instantSurvey.issue_datetime
 * @param {?Number} instantSurvey.job_ref
 * @param {Object} instantSurvey.question_logic
 * @param {Object[]} instantSurvey.questions
 * @param {?Number} [instantSurvey.response_count] included when query has include_response_count=true
 * @param {String} instantSurvey.status
 * @param {?Date} instantSurvey.survey_created_datetime
 * @param {?Number} instantSurvey.survey_ref
 * @param {?Number} instantSurvey.survey_source_id
 * @param {?Number} instantSurvey.survey_template_id
 * @param {String} instantSurvey.title
 * @param {String} instantSurvey.research_objectives
 * @param {Date} instantSurvey.updated_at
 * @param {?Number} instantSurvey.validation_version
 * @return {InstantSurvey}
 */
export const transformInstantSurvey = ({
  country,
  groups,
  id,
  instant_survey_type: instantSurveyType,
  issue_datetime: issueDateTime,
  question_logic: questionLogic,
  questions,
  research_objectives: researchObjectives,
  response_count: responseCount,
  status,
  survey_template_id: surveyTemplateId,
  title,
}) => {
  const transformedQuestions = questions.map(transformQuestion);
  return {
    country,
    groups: groups.map(transformInstantSurveyGroup),
    id,
    instantSurveyType,
    issueDateTime,
    questions: transformedQuestions,
    ...transformQuestionLogic({ questionLogic, questions: transformedQuestions }),
    researchObjectives,
    responseCount,
    status,
    surveyTemplateId,
    title,
  };
};

/**
 * transformInstantSurveyGroup
 * @param {Object} group
 * @param {?String} group.contact_list_group_ref
 * @param {Date} group.created_at
 * @param {?String} group.gen_rep_sample_type
 * @param {Number} group.id
 * @param {Array} group.prompt_answers
 * @param {?String} group.quota_group_definition_ref
 * @param {?String} group.quota_group_ref
 * @param {Number} group.quota_limit
 * @param {?Number} [group.response_count] included when query has include_response_count=true
 * @param {String} group.title
 * @param {Date} group.updated_at
 * @param {?Date} group.validation_datetime
 * @param {String} group.validation_status
 * @param {?Number} group.validation_unique_users
 * @param {?Number} group.validation_users
 * @param {Number} group.validation_version
 * @return {Group}
 */
const transformInstantSurveyGroup = ({
  concept_image,
  concept_name: conceptName,
  continuously_fed: continuouslyFed,
  gen_rep_sample_type: genRepSampleType,
  id,
  launch_method: launchMethod,
  prompt_answers: promptAnswers,
  quota_group_ref: quotaGroupRef,
  quota_limit: quotaLimit,
  response_count: responseCount,
  title: name,
  validation_status: validationStatus,
  validation_unique_users: validationUniqueUsers,
  validation_users: validationUsers,
}) => ({
  conceptImage: concept_image ? transformImageObject(concept_image) : null,
  conceptName,
  continuouslyFed,
  expectedCompletes: validationUsers ? Math.floor(validationUsers / 20) * 10 : null,
  genRepSampleType,
  id,
  launchMethod,
  name,
  promptAnswers: promptAnswers.map(
    ({ fe_notes: feNotes, field, field_type: fieldType, id, operator, prompt_id: promptId }) => ({
      field,
      fieldType,
      id,
      operator,
      promptId,
      values: feNotes?.insights_values,
    })
  ),
  quotaGroupRef,
  quotaLimit,
  responseCount,
  validationStatus,
  validationUniqueUsers,
  validationUsers,
});

/**
 * @typedef {Object} InstantSurveyType
 * @property {Object[]} advancedPrompts
 * @property {String} description
 * @property {String} helpUrl
 * @property {String[][]} hierarchy_search_prompts
 * @property {Number} id
 * @property {String} label
 * @property {String} madlib
 * @property {String} name
 * @property {Object[]} prompts
 */

/**
 * transformInstantSurveyType
 * @param {Object} instantSurveyType
 * @param {Boolean} instantSurveyType.deprecated
 * @param {String} instantSurveyType.description
 * @param {String} [instantSurveyType.help_url]
 * @param {String[][]} instantSurveyType.hierarchy_search_prompts eg: [ ['categories', 'product'] ]
 * @param {String} instantSurveyType.id
 * @param {Object[]} [instantSurveyType.instantSurveyAddons]
 * @param {String} instantSurveyType.label
 * @param {Object} instantSurveyType.modal_prompt_requirements
 * @param {String} instantSurveyType.name
 * @param {String[]} instantSurveyType.panels
 * @param {Number} instantSurveyType.priority
 * @param {Object[]} instantSurveyType.prompts
 * @param {String} instantSurveyType.prompts_madlib
 * @param {Boolean} instantSurveyType.survey_templates_available
 * @return {InstantSurveyType}
 */
export const transformInstantSurveyType = ({
  description,
  help_url: helpUrl = SURVEY_HELP_URL,
  hierarchy_search_prompts,
  id,
  is_specialty,
  label,
  modal_prompt_requirements: modalPromptRequirements,
  name,
  prompts,
  prompts_madlib: madlib,
}) => {
  return {
    advancedPrompts: prompts
      .filter(({ advanced, prompt_key }) => advanced && prompt_key !== 'images') // images is still TBD
      .map(transformSurveyPrompt),
    description,
    helpUrl,
    hierarchy_search_prompts,
    id,
    isSpecialty: is_specialty,
    label,
    madlib,
    modalPromptRequirements,
    name,
    prompts: prompts
      .filter(({ advanced, prompt_key }) => !advanced && prompt_key !== 'images')
      .map(transformSurveyPrompt),
  };
};

/**
 * transformInstantSurveyTypes
 * @param {Object} response
 * @param {Array} response.results
 * @return {Object}
 */
export const transformInstantSurveyTypes = ({ results, ...rest }) => {
  return {
    results: results.map((surveyType) => transformInstantSurveyType(surveyType)),
    ...rest,
  };
};

/**
 * transformQuestion
 * @param {Object} question
 * @param {Boolean} question.ai_generated
 * @param {Number[]} [question.bottom_n_options]
 * @param {String} question.caption
 * @param {Date} question.created_at
 * @param {Boolean} [question.deleted]
 * @param {Number} question.folder
 * @param {Number} question.id
 * @param {Object} [question.image]
 * @param {Number} [question.insights_question_ref]
 * @param {String[]} question.instant_survey_types
 * @param {String} question.model_type
 * @param {Object[]} question.options
 * @param {Object} [question.parameters]
 * @param {Object} question.properties
 * @param {String} [question.properties.best_label]
 * @param {Boolean} [question.properties.force_currency]
 * @param {Boolean} [question.properties.force_numeric]
 * @param {Boolean} [question.properties.force_percent]
 * @param {Number} [question.properties.max_answers]
 * @param {Number} [question.properties.max_number]
 * @param {Number} [question.properties.max_total]
 * @param {Number} [question.properties.min_number]
 * @param {Boolean} [question.properties.must_be_max]
 * @param {Number} [question.properties.number_of_sets]
 * @param {Boolean} [question.properties.only_whole_num]
 * @param {Number} [question.properties.options_per_set]
 * @param {Boolean} [question.properties.randomize_options]
 * @param {String} [question.properties.worst_label]
 * @param {String} [question.question_source_id] question id from the survey source after publish
 * @param {String} question.question_type
 * @param {Boolean} question.randomize_options deprecated as part of INSRV-2367
 * @param {Boolean} question.show_concept_image
 * @param {Object[]} question.sub_questions
 * @param {Boolean} question.survey_question
 * @param {String} question.text
 * @param {String} [question.title]
 * @param {Number[]} [question.top_n_options]
 * @param {Boolean} question.turf
 * @param {String} question.type
 * @param {Date} question.updated_at
 * @return {Question}
 */
export const transformQuestion = ({
  ai_generated: aiGenerated,
  bottom_n_options: bottomNOptions,
  caption,
  folder,
  id,
  image,
  instant_survey_types: instantSurveyTypes,
  options,
  properties,
  question_source_id: questionSourceId,
  question_type: type,
  randomize_options: randomizeOptions,
  show_concept_image: showConceptImage,
  sub_questions: subQuestions,
  text,
  top_n_options: topNOptions,
  turf,
}) => ({
  aiGenerated,
  answers: options.map(
    ({
      anchored: isAnchored,
      exclusive: isExclusive,
      id,
      image,
      none_of_the_above: isNoneOfTheAbove,
      other: isOther,
      text,
    }) => ({
      id,
      image: image ? transformImageObject(image) : null,
      isAnchored,
      isExclusive,
      isNoneOfTheAbove,
      isOther,
      text,
      title: text,
      value: text,
    })
  ),
  bottomNOptions,
  caption,
  folder,
  id,
  image: image ? transformImageObject(image) : null,
  instantSurveyTypes,
  properties: {
    ...(isDefined(properties?.best_label) && { bestLabel: properties.best_label }),
    ...(isDefined(properties?.force_currency) && {
      forceCurrency: properties.force_currency,
    }),
    ...(isDefined(properties?.force_numeric) && {
      forceNumeric:
        // INSRV-3041: Numeric questions will always be force_numeric on the BE, FE only wants one
        // of these options (currency, numeric, percent) to be true
        type === QUESTION_TYPES.NUMERIC.value
          ? !properties?.force_currency && !properties?.force_percent
          : properties.force_numeric,
    }),
    ...(isDefined(properties?.force_percent) && {
      forcePercent: properties.force_percent,
    }),
    ...(isDefined(properties?.max_answers) && { maxAnswers: properties.max_answers }),
    ...(isDefined(properties?.max_number) && { maxNumber: properties.max_number }),
    ...(isDefined(properties?.max_total) && { maxTotal: properties.max_total }),
    ...(isDefined(properties?.min_number) && { minNumber: properties.min_number }),
    ...(isDefined(properties?.must_be_max) && { mustBeMax: properties.must_be_max }),
    ...(isDefined(properties?.number_of_sets) && {
      numberOfSets: properties.number_of_sets,
    }),
    ...(isDefined(properties?.only_whole_num) && {
      onlyWholeNum: properties.only_whole_num,
    }),
    ...(isDefined(properties?.options_per_set) && {
      optionsPerSet: properties.options_per_set,
    }),
    // preserve randomizedOptions true when reading existing questions
    randomizeOptions: properties?.randomize_options || randomizeOptions,
    ...(isDefined(properties?.worst_label) && { worstLabel: properties.worst_label }),
  },
  questionSourceId: questionSourceId ? Number(questionSourceId) : questionSourceId,
  showConceptImage,
  subQuestions: subQuestions.map(transformQuestion),
  text,
  topNOptions,
  turf,
  type: turf ? QUESTION_TYPES.TURF.value : type,
});

/**
 * transformQuestionFolder
 * @param {Object} folder
 * @param {Object[]} folder.contents
 * @param {Number} folder.count
 * @param {Object} folder.current_questionfolder
 * @param {Number} folder.limit
 * @param {Number} folder.offset
 * @param {Object} [folder.parent_folder]
 * @return {Object}
 */
export const transformQuestionFolder = ({ contents, count, limit, offset }) => ({
  contents: contents.map((content) => {
    switch (content.type) {
      case MODEL_TYPES.QUESTION:
        return transformQuestion(content);
      case MODEL_TYPES.QUESTION_FOLDER:
        // `isFolder` still necessary while using existing ModalQuestionsLibrary/components/Results
        return { ...content, isFolder: true, text: content.title };
      default:
        return content;
    }
  }),
  count,
  limit,
  offset,
});

/**
 * transformSurveyTemplate
 * @param {Object} templates
 * @param {Number} templates.count
 * @param {Object[]} templates.results
 * @return {Object}
 */

export const transformSurveyTemplates = ({ count, results }) => ({
  count,
  results: results.map((surveyTemplate) => {
    return { ...surveyTemplate, questions: surveyTemplate.questions.map(transformQuestion) };
  }),
});

/**
 * transformQuestionLogic
 * @param {Object} options.questionLogic
 * @param {Question[]} options.questions
 * @return {Object}
 */
export const transformQuestionLogic = ({ questionLogic, questions }) => {
  let hasQuestionLogicError = false;
  const modifiedQuestionLogic = cloneDeep(questionLogic);
  for (const questionId in modifiedQuestionLogic) {
    const question = modifiedQuestionLogic[questionId];
    if (question.disqualify) {
      question.disqualify.operator = QUESTION_LOGIC_OPERATORS[question.disqualify.operator];
      const disqualifyQuestion = questions.find((question) => question.id === parseInt(questionId));
      const initialAnswersLength = question.disqualify.answers.length;
      question.disqualify.answers = question.disqualify.answers
        .map((answerId) => {
          return disqualifyQuestion.answers.find((answer) => answer.id === answerId);
        })
        .filter((answer) => !!answer);
      if (
        question.disqualify.answers.length === 0 ||
        question.disqualify.answers.length !== initialAnswersLength
      ) {
        hasQuestionLogicError = true;
      }
    }
    if (question.skip) {
      question.skip.operator = QUESTION_LOGIC_OPERATORS[question.skip.operator];
      // eslint-disable-next-line no-loop-func
      question.skip.logic = question.skip.logic.map((row) => {
        if (row.url_variable === 'quota_group_ref') {
          if (row.values.length === 0) {
            hasQuestionLogicError = true;
          }
          return {
            operator: QUESTION_LOGIC_OPERATORS[row.operator],
            url_variable: 'quota_group_ref',
            values: row.values,
          };
        } else if (row.comparison_value) {
          const skipQuestion = questions.find((question) => question.id === row.question);
          if (!row.comparison_value) {
            hasQuestionLogicError = true;
          }

          return {
            comparison_value: row.comparison_value,
            operator: QUESTION_LOGIC_OPERATORS[row.operator],
            question: skipQuestion,
          };
        } else {
          const skipQuestion = questions.find((question) => question.id === row.question);
          const answers = row.answers
            .map((answerId) => {
              return skipQuestion.answers.find((answer) => answer.id === answerId);
            })
            .filter((answer) => !!answer);
          if (answers.length === 0 || answers.length !== row.answers.length) {
            hasQuestionLogicError = true;
          }

          return {
            answers,
            operator: QUESTION_LOGIC_OPERATORS[row.operator],
            question: skipQuestion,
          };
        }
      });
    }
  }
  return { hasQuestionLogicError, questionLogic: modifiedQuestionLogic };
};
